"use client"

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { useAiAnswer, type AiTurn } from "@/components/chat/use-ai-answer"
import { useChatSession } from "@/components/chat/use-chat-session"
import { usePageScopedChat } from "@/components/chat/chat-launcher-context"
import { LAUNCHER_TEAM } from "@/components/chat/launcher-team"
import { ChatMessage } from "@/components/chat/chat-message"
import { ChatPanel } from "@/components/chat/chat-panel"
import { EscalationAction } from "@/components/chat/escalation-action"
import { SparkleIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"

export interface BiomarkersAiDrawerProps {
  open: boolean
  onClose: () => void
  /** Question to ask when the drawer opens. Re-asked whenever `askNonce`
   *  changes (so the same text can be re-sent from a chip). */
  question: string | null
  askNonce: number
  scope: string
  country: string
  locale: string
  pageUrl: string
  pageTitle: string
}

type Tab = "ai" | "team"

const sendIcon = (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

// The "Talk to team" tab wears the real team faces (same people the site's
// chat launcher shows) instead of a generic person glyph.
const teamAvatars = (
  <span className="flex items-center -space-x-1.5" aria-hidden="true">
    {LAUNCHER_TEAM.slice(0, 3).map((member) => (
      <span
        key={member.src}
        className="size-[18px] overflow-hidden rounded-full border-2 border-white ring-1 ring-black/[0.04]"
      >
        <Image
          src={member.src}
          alt=""
          width={18}
          height={18}
          className="h-full w-full object-cover"
        />
      </span>
    ))}
  </span>
)

export function BiomarkersAiDrawer({
  open,
  onClose,
  question,
  askNonce,
  scope,
  country,
  locale,
  pageUrl,
  pageTitle,
}: BiomarkersAiDrawerProps) {
  const t = useTranslations("chat")
  const bt = useTranslations("biomarkers")

  const [tab, setTab] = useState<Tab>("ai")

  // While the drawer is open, suppress the site-wide floating launcher so the
  // two chat surfaces never overlap (this drawer becomes the single one).
  usePageScopedChat(open)

  // Shared Chatwoot-backed session for the "Talk to team" tab (ChatPanel).
  const chatSession = useChatSession({ country, locale })

  const { turns, isStreaming, error, status, ask } = useAiAnswer({
    scope,
    country,
    locale,
    pageUrl,
    pageTitle,
  })

  // Ask the incoming question once per nonce (chip / search entry) — always
  // lands on the AI tab.
  const askedNonce = useRef(0)
  useEffect(() => {
    if (!open || !question || askNonce === askedNonce.current) return
    askedNonce.current = askNonce
    setTab("ai")
    void ask(question)
  }, [open, question, askNonce, ask])

  // Follow-up composer (AI tab).
  const [draft, setDraft] = useState("")
  const draftRef = useRef<HTMLTextAreaElement>(null)
  const autosize = useCallback(() => {
    const el = draftRef.current
    if (!el) return
    el.style.height = "0px"
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [])
  useEffect(autosize, [draft, autosize])

  const submitDraft = useCallback(() => {
    const v = draft.trim()
    if (!v) return
    setDraft("")
    void ask(v)
  }, [draft, ask])

  const onDraftKey = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        submitDraft()
      }
    },
    [submitDraft]
  )

  // Esc closes; focus the AI composer shortly after opening.
  useEffect(() => {
    if (!open) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    const h = window.setTimeout(() => {
      if (tab === "ai") draftRef.current?.focus()
    }, 400)
    return () => {
      document.removeEventListener("keydown", onKey)
      window.clearTimeout(h)
    }
  }, [open, onClose, tab])

  // Stick the streaming answer to the bottom.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [turns])

  const statusLabel = ((): string | undefined => {
    if (!status) return undefined
    if (
      status.phase === "searching" &&
      status.index != null &&
      status.total != null
    ) {
      return t("status.searching", {
        index: String(status.index),
        total: String(status.total),
      })
    }
    if (status.phase === "searching") return t("status.retrieving")
    if (status.phase === "understanding") return t("status.understanding")
    return t("status.retrieving")
  })()

  const lastUserMessage = (() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "user") return turns[i].content
    }
    return ""
  })()

  const renderTurn = (turn: AiTurn, i: number) => (
    <ChatMessage
      key={turn.messageId ?? `${turn.role}-${i}`}
      role={turn.role}
      content={turn.content}
      isStreaming={turn.isStreaming}
      thinkingLabel={turn.isStreaming ? statusLabel : undefined}
      sources={turn.sources}
      sourcesLabel={t("ui.sources")}
    />
  )

  const tabButton = (id: Tab, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      aria-pressed={tab === id}
      className={cn(
        "type-title-sm flex cursor-pointer items-center gap-1.5 rounded-full px-3.5 py-1.5 transition-colors",
        tab === id
          ? "bg-white text-neutral-900"
          : "text-neutral-500 hover:text-neutral-900"
      )}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <>
      {/* Dim overlay (mobile) */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-[9999] bg-black/25 transition-opacity duration-300 md:bg-transparent",
          open ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      />

      {/* Right drawer */}
      <aside
        aria-label={bt("askAi")}
        aria-hidden={!open}
        className={cn(
          "fixed top-0 right-0 z-[10000] flex h-full w-full flex-col bg-white transition-transform duration-[380ms] ease-[cubic-bezier(.22,1,.36,1)] sm:w-[440px]",
          // Shadow only while open — it casts leftward (-18px + 48px blur), so if
          // it stayed on the closed (off-screen) drawer it would bleed a purple
          // strip onto the page's right edge.
          open
            ? "translate-x-0 shadow-[-18px_0_48px_-24px_rgba(58,31,57,0.28)]"
            : "translate-x-full"
        )}
      >
        {/* Header — tab switch + close */}
        <div className="flex h-16 flex-none items-center justify-between border-b border-neutral-200 px-4">
          <div className="flex items-center rounded-full bg-neutral-100 p-0.5">
            {tabButton(
              "ai",
              bt("askAi"),
              <SparkleIcon width={16} height={16} />
            )}
            {tabButton("team", bt("talkToTeam"), teamAvatars)}
          </div>
          <button
            type="button"
            aria-label={t("assistant.close")}
            onClick={onClose}
            className="flex size-8 cursor-pointer items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Ask AI tab ── */}
        {tab === "ai" && (
          <>
            <div
              ref={scrollRef}
              className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-5"
            >
              {turns.length === 0 && (
                <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
                  {t("assistant.subtitle")}
                </p>
              )}
              {turns.map(renderTurn)}
              {error && (
                <button
                  type="button"
                  onClick={() => lastUserMessage && ask(lastUserMessage)}
                  className="type-body-md mt-1 self-start font-medium text-neutral-900 underline underline-offset-4"
                >
                  {t("assistant.retry")}
                </button>
              )}
            </div>

            <div className="flex-none border-t border-neutral-200 px-4 pt-3 pb-4">
              <div className="flex items-end gap-2">
                <textarea
                  ref={draftRef}
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onDraftKey}
                  placeholder={t("assistant.placeholder")}
                  aria-label={t("assistant.inputAria")}
                  className="type-body-md max-h-40 min-h-[44px] flex-1 resize-none rounded-2xl bg-neutral-100 px-4 py-3 text-neutral-900 transition-shadow outline-none placeholder:text-neutral-400 focus:ring-2 focus:ring-neutral-300"
                />
                <button
                  type="button"
                  aria-label={t("assistant.ask")}
                  onClick={submitDraft}
                  className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-2xl bg-neutral-900 text-white transition-colors hover:bg-neutral-800 active:scale-[0.97]"
                >
                  {sendIcon}
                </button>
              </div>
              <div className="mt-2.5 flex items-center justify-between gap-3">
                <EscalationAction
                  surface="detail"
                  scope={scope}
                  onContinueInChat={() => setTab("team")}
                />
                <span className="type-body-sm text-neutral-400">
                  {t("assistant.disclaimer")}
                </span>
              </div>
              {isStreaming && (
                <span className="sr-only" role="status">
                  {statusLabel ?? t("assistant.subtitle")}
                </span>
              )}
            </div>
          </>
        )}

        {/* ── Talk to team tab (real ChatPanel, header supplied by drawer) ── */}
        {tab === "team" && (
          <div className="min-h-0 flex-1">
            <ChatPanel
              scope={scope}
              country={country}
              locale={locale}
              pageUrl={pageUrl}
              pageTitle={pageTitle}
              entryPoint="launcher"
              onClose={onClose}
              chatSession={chatSession}
              hideHeader
            />
          </div>
        )}
      </aside>
    </>
  )
}
